// src/pages/Last9MonthSales.jsx

// ---------------------- IMPORTS ----------------------

import "./Last9MonthSales.css";

// React core: used for building the component and using hooks
import React, { useEffect, useState } from "react";

// Auth0 hook: gives us the logged-in user and a way to get an access token
import { useAuth0 } from "@auth0/auth0-react";

// React Router: used for the "Back to Portal" link & parameters that may be passed from parent route (aka Portal.jsx)
import { Link , useOutletContext } from "react-router-dom";


// ---------------------- COMPONENT (whole page) ----------------------

/**
 * Page: Last9MonthSales
 *
 * Purpose:
 * - Show the "Top 100 Hospitals (last 9 months)" metric.
 * - This page assumes it is already wrapped in <ProtectedRoute> in App.jsx,
 *   so it does NOT worry about login / redirect logic.
 * - It only:
 *    1. Fetches metrics thru OutletContext via Portal.jsx(/api/metrics)
 *    2. Reads metrics.ServerData.
 *    3. Renders a table of 100 Hospitals by Sales and a breakdown of each of the last 9 months for each in Top 100.
 */

// ---------------------- DATA FETCH  ----------------------

const Last9MonthSales = () => {

  const { 
              user,
              last9MonthsSalesObject,
              loading,
              error,
              niceMoney,
          } =useOutletContext(); // Get data from OutletContext provided by Portal.jsx

  // ---------------------- DATA EXTRACTION ----------------------

  console.log("Last9MonthsSalesObject:", last9MonthsSalesObject);

  // Array of top 100 Hospitals over last 9 months. Each element is expected to be: { customer: string, sales: number }
  const topRows = last9MonthsSalesObject?.topHospitals9Months || [];
  // console.log("Top Rows:", topRows);

   // Object of all individual months ago. Each Hospital element in each month is expected to be: { customer: string, sales: number }
  const topHospitalsEachMonthSales = last9MonthsSalesObject?.monthBuckets || [];
  console.log("Top Hospitals Each Month Sales:", topHospitalsEachMonthSales);

  const xMonthsAgo = last9MonthsSalesObject?.DateRangeInfo?.monthRanges || [];
  // console.log("X Months Ago Labels:", xMonthsAgo);

 

  //Each element is expected to be: { customer: string, sales: number }
  const month9Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth9 || [];
  const month8Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth8 || [];
  const month7Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth7 || [];
  const month6Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth6 || [];
  const month5Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth5 || [];
  const month4Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth4 || [];
  const month3Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth3 || [];
  const month2Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth2 || [];
  const month1Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth1 || [];
  const month0Sales = last9MonthsSalesObject?.monthBuckets.eachHospitalsSalesMonth0 || [];


  // ---------------------- HELPERS ----------------------

  // Helper to get the sales value for a specific month index, or 0 if not found
  const getMonthVal = (arr, idx) => arr?.[idx]?.sales ?? 0;

  // Helper to determine the CSS class for a month cell based on its value vs average
  const monthCellClass = (val, avg) => {
    if (val === 0) return "cell-zero";     // red
    if (val > avg) return "cell-above";    // green
    return "cell-below";                  // yellow (includes val < avg and ties)
  };

  const monthsBack = xMonthsAgo.length - 1;
  // console.log("Months Back:", monthsBack);



  // -------------- Debugging: log the rows to the console -------------

//  console.log("Top Hospitals (last 9 months):", topRows); 
  // console.log("Each Month Sales for Top Hospitals:", topHospitalsEachMonthSales);
  // console.log("X Months Ago Labels:", xMonthsAgo);
  // console.log("Month 9 Sales:", month9Sales);
  // console.log("Month 8 Sales:", month8Sales);
  // console.log("Month 7 Sales:", month7Sales);
  // console.log("Month 6 Sales:", month6Sales);
  // console.log("Month 5 Sales:", month5Sales);
  // console.log("Month 4 Sales:", month4Sales);
  // console.log("Month 3 Sales:", month3Sales);
  // console.log("Month 2 Sales:", month2Sales);
  // console.log("Month 1 Sales:", month1Sales);
  // console.log("Month 0 Sales:", month0Sales);


  // ---------------------- RENDER ----------------------

  return (
    <main className="last9MonthsSalesPage" style={{ padding: "4rem 1rem", textAlign: "center" }}>
      {/* Page title */}
      <h1>Top 100 Hospitals - (Last 9 Months):</h1>

      {/* Friendly "Welcome" line if we have user info from Auth0 */}
      {user && <p>Welcome, {user.name}</p>}

      {/* Simple navigation link back to the main Portal page */}
      <p style={{ marginTop: "0.5rem" }}>
        <Link className="back-link" to="/portal">← Back to Client Portal</Link>
      </p>

      {/* Show loading state while talking to backend */}
      {loading && <p>Loading metrics…</p>}

      {/* Show error, if any */}
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* If not loading, no error, and no rows, tell the user the window is empty */}
      {!loading && !error && topRows.length === 0 && (
        <p>No data found in the last 9 months window.</p>
      )}


      {/* Legend for the table cells */}
      <div className="legend"> 
        <h3>Legend:</h3>
        <p>Value ABOVE 9 month Average: <span className="cell-above">Green</span></p>
        <p>Value BELOW 9 month Average: <span className="cell-below">Yellow</span></p>
        <p>$0: <span className="cell-zero">Red</span></p>
      </div>


      {/* Main data table: only render when we have rows */}
      {!loading && !error && topRows.length > 0 && (
        <div className="last9MonthsSalesTableWrap">
          <table
            className="last9MonthsSalesTable"
            style={{
              margin: "2rem auto 0",
              borderCollapse: "collapse",
            }}
          >
            <thead>
              <tr>
                <th></th>
                <th></th>
                <th></th>
                {
                  xMonthsAgo.map((monthObj, idx) => (
                  <th key={idx} style={{
                    borderBottom: "1px solid #ccc",
                    padding: "0.25rem 0.75rem",
                    textAlign: "center",
                    whiteSpace: "nowrap",     // <-- prevents wrapping
                  }}>
                    {monthObj.startDate}<br/> - <br/>{monthObj.endDate}
                  </th>
                  ))
                }
              </tr>
              <tr>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "0.25rem 0.75rem",
                    textAlign: "left",
                  }}
                >
                  Customer (Hospital)
                </th>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "0.25rem 0.75rem",
                    textAlign: "center",
                    whiteSpace: "nowrap",     // <-- prevents wrapping
                  }}
                >
                  Sales<br/> (Last 9 Months)
                </th>
                <th
                  style={{
                    borderBottom: "1px solid #ccc",
                    padding: "0.25rem 0.75rem",
                    textAlign: "center",
                    whiteSpace: "nowrap",     // <-- prevents wrapping
                  }}
                >
                  Average Monthly Sales<br/> (last 9 months)
                </th>
                {
                  xMonthsAgo.map((monthObj, idx) => (
                  <th key={idx} style={{
                    borderBottom: "1px solid #ccc",
                    padding: "0.25rem 0.75rem",
                    textAlign: "center",
                    
                  }}>
                    {monthObj.label}
                  </th>
                  ))
                }
              </tr>
            </thead>
            <tbody>
              {topRows.map((row, idx) => (
                <tr key={`${row.customer}-${idx}`}>
                  <td
                    style={{
                      borderBottom: "1px solid #eee",
                      padding: "0.25rem 0.75rem",
                      textAlign: "left",
                    }}
                  >
                    {row.customer}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #eee",
                      padding: "0.25rem 0.75rem",
                      textAlign: "center",
                    }}
                  >
                    {niceMoney(row.sales)}
                  </td>
                  <td
                    style={{
                      borderBottom: "1px solid #eee",
                      padding: "0.25rem 0.75rem",
                      textAlign: "center",
                    }}
                  >
                    {niceMoney(row.sales / 9)} 
                  </td>

                {/* Month cells Individually */}
                {/* <td
                  style={{
                    borderBottom: "1px solid #eee",
                    padding: "0.25rem 0.75rem",
                    textAlign: "center",
                  }}
                  className={monthCellClass(getMonthVal(month9Sales, idx), row.sales / 9)}
                >
                  {niceMoney(month9Sales[idx]?.sales)} 
                </td> */}

                {/* Month cells Dynamically  */}
                {topHospitalsEachMonthSales.map((monthObj) => {
                  const val = monthObj.eachHospitalsSales?.[idx]?.sales ?? 0;

                  return (
                    <td
                      key={monthObj.offset}
                      style={{
                        borderBottom: "1px solid #eee",
                        padding: "0.25rem 0.75rem",
                        textAlign: "center",
                      }}
                      className={monthCellClass(val, row.sales / 9)}
                    >
                      {niceMoney(val)}
                    </td>
                  );
                })}

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
};


export default Last9MonthSales;
